import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, X } from "lucide-react";

/** GIPHY-powered GIF picker. Calls the backend proxy (key stays server-side). */
export default function GifPicker({ onChoose, onClose, accent = "#ff4500" }) {
    const [q, setQ] = useState("");
    const [gifs, setGifs] = useState([]);
    const [loading, setLoading] = useState(true);
    const timer = useRef(null);

    const load = async (path) => {
        setLoading(true);
        try {
            const { data } = await api.get(path);
            setGifs(data.data || []);
        } catch {
            setGifs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load("/gifs/trending"); }, []);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        const term = q.trim();
        timer.current = setTimeout(() => {
            load(term ? `/gifs/search?q=${encodeURIComponent(term.slice(0, 50))}` : "/gifs/trending");
        }, 450);
        return () => timer.current && clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    return (
        <div className="absolute z-30 mt-2 w-[320px] max-w-[90vw] border border-[#2a2a2a] bg-[#0f0f0f] shadow-2xl" data-testid="gif-picker">
            <div className="flex items-center gap-2 p-2 border-b border-[#2a2a2a]">
                <Search className="h-4 w-4 text-[#a0a0a0]" />
                <input
                    autoFocus
                    data-testid="gif-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search GIPHY"
                    className="flex-1 bg-transparent text-sm text-white outline-none"
                />
                <button type="button" onClick={onClose} data-testid="gif-close-btn" className="text-[#a0a0a0] hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="h-[260px] overflow-y-auto p-2">
                {loading ? (
                    <div className="text-center text-xs text-[#a0a0a0] py-10">Loading GIFs…</div>
                ) : gifs.length === 0 ? (
                    <div className="text-center text-xs text-[#a0a0a0] py-10">No GIFs found.</div>
                ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                        {gifs.map((g) => (
                            <button
                                key={g.id}
                                type="button"
                                data-testid={`gif-result-${g.id}`}
                                onClick={() => onChoose({ preview_url: g.preview_url, full_url: g.full_url })}
                                className="overflow-hidden rounded border border-transparent hover:border-2"
                                style={{ borderColor: accent }}
                            >
                                <img src={g.preview_url} alt={g.title || "GIF"} loading="lazy" className="h-20 w-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className="text-[9px] text-[#666] text-center py-1 border-t border-[#2a2a2a]">Powered by GIPHY</div>
        </div>
    );
}
